import React, { useEffect, useRef, useState } from "react";
import ReactTimeout from 'react-timeout'
import ServerProvider from 'components/provider/ServerProvider'
import CacheProvider from 'components/provider/CacheProvider'
import SplashProvider, { useSplash } from 'components/provider/SplashProvider'
import { useDispatch, useSelector } from 'react-redux'
import { useIsInManagement } from 'utils/helper-linking'
import ConfigProvider, { configClient } from 'components/provider/ConfigProvider'
import { authenticationConstants } from 'services/redux/authentication/constants'
import { dataConstants } from 'services/redux/data/constants'
import selectEmployeeLanguage from 'services/redux/data/selectors/selectEmployeeLanguage'
import SecureStoreService from 'services/store/secure-store'
import { storeKeys } from 'services/store/keys'
import FetchEmployee from 'utils/fetch/FetchEmployee'
import { SET_AUTHENTICATION, SET_DATA } from 'services/redux/action-types'
import { setIsInManagement } from 'services/redux/data/actions'
import _ from 'lodash'
import LoginCheckError from 'components/molecules/LoginCheckError'
import HelperString from 'utils/helper-string'
import { constantsActive } from 'utils/constants/constants'
import HelperPlatform from 'utils/helper-platform'
import ObjectEmployee from 'utils/objects/employee'
import store from 'services/redux/store'
import { MODES } from 'utils/constants/app-modes'
import NotificationNavigator from 'navigations/notifications'
import ManagementNavigator from 'navigations/management'
import AuthenticationNavigator from 'navigations/authentication'
import ObjectVersion from 'utils/objects/version'
import HomeOnboardingIndexScene from 'scenes/home/onboarding'
import HomeNavigator from 'navigations/home'
import HelperDetect from 'utils/helper-detect'
import HelperDevelopment from 'utils/helper-development'
import PushNotificationsProvider from 'services/push-notifications/Provider'
import { StyleSheet, View } from 'react-native'

const IndexScene = ({ assetsLoaded = true, startupReady = true }) => {

	return (
		<SplashProvider>

			{ startupReady && (
				<ConfigProvider
					config={ configClient }>

					<ServerProvider>

						<CacheProvider>

							<IndexUserScene assetsLoaded={ assetsLoaded } />
						</CacheProvider>
					</ServerProvider>
				</ConfigProvider>
			) }
		</SplashProvider>
	);
}

const IndexUserScene = ({ assetsLoaded }) => {
	
	const dispatch = useDispatch()
	const isInManagement = useIsInManagement()
	const { fadeOutSplash, splashVisible, logout } = useSplash()
	
	const _loggedIn = useSelector( state => {
		return state.authentication[authenticationConstants.LOGGED_IN];
	} )
	const _tokenRevalidate = useSelector( state => {
		return state.authentication[authenticationConstants.TOKEN_REVALIDATE];
	} )
	const _mode = useSelector( state => {
		return state.data[dataConstants.MODE];
	} )
	const _employee = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE];
	} )
	const _employeeUser = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE]?.user;
	} )
	const _employeeLanguage = useSelector( selectEmployeeLanguage )
	const _alert = useSelector( state => {
		return state.data[dataConstants.ALERT];
	} );
	
	const isFetchingEmployee = useRef( false )
	
	// This scene is remounted when the web layout crosses the mobile breakpoint.
	// Keep an already authenticated Redux session out of the splash/loading state.
	const [ loading, setLoading ] = useState( !_loggedIn )
	const [ shownOnboarding, setShownOnboarding ] = useState( null )
	
	const setIsFetchingEmployee = ( value ) => {
		isFetchingEmployee.current = value
	}
	
	const logoutCallback = () => {
		setLoading( false )
	}
	
	const fetchEmployee = ({callback} = {}) => {
		if( isFetchingEmployee.current === true || _loggedIn === true ) {
			return;
		}
		
		const onCallback = () => {
			if( callback ) {
				setTimeout( callback, 1000 );
			}
		}
		
		setIsFetchingEmployee( true );
		
		SecureStoreService
			.get( storeKeys.EMPLOYEE_GUID )
			.then( employeeGuid => {
				FetchEmployee.fetch( employeeGuid, {
					callbackSuccess: ( { employee }) => {
						dispatch({
							type: SET_DATA,
							key: dataConstants.EMPLOYEE,
							payload: employee
						});
						
						dispatch({
							type: SET_AUTHENTICATION,
							key: authenticationConstants.LOGGED_IN,
							payload: true
						});
						
						setIsFetchingEmployee( false );
						setLoading( false )
						onCallback()
					},
					callbackError: ( error, response ) => {
						if( response?.error === 'user_not_found_1' ) {
							logoutCallback();
						}
						
						setIsFetchingEmployee( false );
						setLoading( false )
						onCallback()
					},
					dispatchDataToStore: true,
					instant: true
				});
			});
	}
	
	const retryValidateLogin = ( {callback} ) => {
		fetchEmployee({ callback })
	}
	
	useEffect( () => { // This trigger might only be needed when we're done  loading the app
		if( !_loggedIn ) {
			fetchEmployee()
		}
	}, [ _loggedIn ] )
	
	useEffect( () => {
		dispatch( setIsInManagement( isInManagement ) )
	}, [ isInManagement ] )
	
	useEffect( () => {
		if( splashVisible && !loading && assetsLoaded ) {
			fadeOutSplash()
		}
	}, [ loading, splashVisible, assetsLoaded ] )
	
	// Only show this when we have to re-validate and we are not the scanner!
	if( _tokenRevalidate === true && !ObjectEmployee.isScanner( _employee ) ) {
		return (
			<LoginCheckError
				key={ HelperString.hash("login-token-check", _employeeLanguage?.code ) }
				action={ retryValidateLogin }
				logoutCallback={ logoutCallback }
				error={ 'token_revalidate' }
			/>
		);
	}
	
	// Check if the app is loaded in puppeteer or some sorts
	HelperDetect.isAutomated();
	
	// Init the required content
	const content = [
		_alert
	]
	
	if( !loading && assetsLoaded ) {
		// Check if we are already in a management environment
		const hasActiveUser = _.get( _employeeUser, [ 'active' ], constantsActive.NO ) === constantsActive.YES;
		
		let showNotifications = true;
		if( HelperDevelopment.isDev() || HelperPlatform.isWeb() || ObjectEmployee.isOndmand( store.getState().data[ dataConstants.EMPLOYEE ] ) ) {
			showNotifications = false;
		}
		
		switch ( true ) {
			case _loggedIn && _mode === MODES.NOTIFICATIONS && showNotifications:
				content.push(
					<NotificationNavigator
						key={ HelperString.hash("notification_navigator", _employeeLanguage?.code ) }
					/>
				)
				break;
			
			case _mode === MODES.MANAGEMENT:
			case isInManagement && hasActiveUser:
				switch( true ) {
					case _loggedIn && hasActiveUser:
						content.push(
							<ManagementNavigator
								key={ HelperString.hash( "management_navigator", _employeeLanguage?.code ) }
							/>
						);
						break;
					
					default:
						content.push(
							<AuthenticationNavigator
								key={ HelperString.hash( "authentication_navigator", _employeeLanguage?.code ) }
							/>
						);
				}
				break;
			
			default:
				const showOnboarding = ObjectVersion.mustShowUpdate();
				
				switch( true ) {
					case _loggedIn && !HelperPlatform.isWeb() && ( shownOnboarding === null && showOnboarding !== false ):
						content.push(
							<HomeOnboardingIndexScene
								key={ HelperString.hash( "onboarding_scene", _employeeLanguage?.code ) }
								data={ showOnboarding }
								onDone={ () => {
									setShownOnboarding( false )
								} }
							/>
						)
						break;
					
					case _loggedIn:
						content.push(
							<HomeNavigator
								key={ HelperString.hash(  "home_navigator", _employeeLanguage?.code ) }
							/>
						)
						break;
					
					default:
						content.push(
							<AuthenticationNavigator
								key={ HelperString.hash( "authentication_navigator", _employeeLanguage?.code ) }
							/>
						)
				}
		}
	}
	
	return (
		<PushNotificationsProvider>
			<View
				style={staticStyles.viewHeight999}>

				{ content }
			</View>
		</PushNotificationsProvider>
	)
}

const staticStyles = StyleSheet.create({
	viewHeight999: {
		height: '99.9%',
	},
});

export default ReactTimeout( IndexScene )
